import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Crown, CheckCircle2, Landmark, Clock, Ticket } from "lucide-react";
import { Input } from "../components/ui/input";
import { PlanAppyPayCard } from "../components/payments/PlanAppyPayCard";

export default function PlanPage() {
  const { token, user, isAdmin, refreshUser } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [saving, setSaving] = useState(false);
  const [keyCode, setKeyCode] = useState("");
  const [activating, setActivating] = useState(false);

  useEffect(() => { fetchPlan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlan = async () => {
    try {
      const res = await axios.get(`${API_URL}/company/plan`, { headers, withCredentials: true });
      setData(res.data);
      setCycle(res.data.billing_cycle || "monthly");
    } catch { toast.error("Erro ao carregar plano"); }
  };

  const choosePlan = async (planId) => {
    if (!isAdmin) { toast.error("Apenas administradores podem mudar o plano"); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/company/plan`, { plan: planId, billing_cycle: cycle }, { headers, withCredentials: true });
      toast.success(`Plano alterado para ${planId === "bronze" ? "LUMORA Bronze" : "LUMORA Ouro"}. Os menus foram atualizados.`);
      await fetchPlan();
      await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao mudar plano"); }
    finally { setSaving(false); }
  };

  const activateKey = async () => {
    if (!keyCode.trim()) { toast.error("Insira a chave de ativação"); return; }
    setActivating(true);
    try {
      const res = await axios.post(`${API_URL}/company/activate-key`, { code: keyCode.trim() }, { headers, withCredentials: true });
      toast.success(res.data.message || "Subscrição ativada com sucesso!");
      setKeyCode("");
      await fetchPlan();
      await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao ativar a chave"); }
    finally { setActivating(false); }
  };

  if (!data) {
    return <div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const statusBadge = data.plan_status === "trial"
    ? { label: `Período de teste — ${data.trial_days_left} dia(s) restante(s)`, cls: "bg-amber-500/20 text-amber-600" }
    : data.plan_status === "active"
    ? { label: data.subscription_ends_at ? `Ativo até ${new Date(data.subscription_ends_at).toLocaleDateString("pt-PT")} (${data.subscription_days_left}d)` : "Ativo", cls: "bg-emerald-500/20 text-emerald-600" }
    : data.plan_status === "suspended"
    ? { label: "Suspenso", cls: "bg-zinc-500/20 text-zinc-500" }
    : { label: "Expirado", cls: "bg-red-500/20 text-red-500" };

  return (
    <div className="space-y-6" data-testid="plan-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">O Meu Plano</h1>
        <p className="text-muted-foreground">Gestão da subscrição LUMORA da sua empresa</p>
      </div>

      <Card className="border-primary/30">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10"><Crown className="w-6 h-6 text-primary" /></div>
            <div>
              <p className="font-bold text-lg" data-testid="current-plan-name">{data.plan === "bronze" ? "LUMORA Bronze" : "LUMORA Ouro"}</p>
              <p className="text-sm text-muted-foreground">Ciclo: {data.billing_cycle === "annual" ? "Anual (−20%)" : "Mensal"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={statusBadge.cls} data-testid="plan-status-badge">
              <Clock className="w-3.5 h-3.5 mr-1" /> {statusBadge.label}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <PlanAppyPayCard headers={headers} cycle={cycle} onPaid={async () => { await fetchPlan(); refreshUser?.(); }} />
      )}

      {isAdmin && (
        <Card className="border-cyan-500/30 bg-cyan-500/5" data-testid="activation-key-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-cyan-500" />
              <p className="font-semibold">Chave de Ativação</p>
            </div>
            <p className="text-sm text-muted-foreground">
              Recebeu uma chave após o pagamento? Insira-a aqui para ativar a subscrição de imediato
              (mensal = 30 dias, anual = 365 dias).
            </p>
            <div className="flex gap-2 flex-wrap">
              <Input
                value={keyCode}
                onChange={(e) => setKeyCode(e.target.value.toUpperCase())}
                placeholder="LUM-XXXX-XXXX-XXXX"
                className="font-mono max-w-xs"
                data-testid="activation-key-input"
              />
              <Button disabled={activating || !keyCode.trim()} onClick={activateKey} data-testid="activate-key-btn">
                {activating ? "A ativar..." : "Ativar Subscrição"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => setCycle("monthly")}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cycle === "monthly" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
          data-testid="cycle-monthly-btn"
        >
          Mensal
        </button>
        <button
          onClick={() => setCycle("annual")}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cycle === "annual" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
          data-testid="cycle-annual-btn"
        >
          Anual <span className="ml-1 text-xs opacity-80">−20%</span>
        </button>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        {(data.plans || []).map((p) => {
          const isCurrent = data.plan === p.plan_id && data.billing_cycle === cycle;
          const price = cycle === "annual" ? p.price_annual_monthly : p.price_monthly;
          return (
            <Card key={p.plan_id} className={p.highlight ? "border-primary shadow-lg" : ""} data-testid={`plan-card-${p.plan_id}`}>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-xl">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.tagline}</p>
                  </div>
                  {p.highlight && <Badge className="bg-primary/20 text-primary">Mais Completo</Badge>}
                </div>
                <div>
                  <p className="text-3xl font-black font-mono">{formatCurrency(price)}<span className="text-sm font-normal text-muted-foreground"> /mês</span></p>
                  {cycle === "annual" && <p className="text-xs text-muted-foreground">Faturado anualmente: {formatCurrency(p.price_annual_total)} (poupa 20%)</p>}
                </div>
                <ul className="space-y-1.5">
                  {p.features.slice(0, 7).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /> {f}</li>
                  ))}
                  {p.features.length > 7 && <li className="text-xs text-muted-foreground pl-6">+{p.features.length - 7} mais funcionalidades</li>}
                </ul>
                <Button
                  className="w-full"
                  variant={isCurrent ? "outline" : "default"}
                  disabled={isCurrent || saving || !isAdmin}
                  onClick={() => choosePlan(p.plan_id)}
                  data-testid={`choose-plan-${p.plan_id}`}
                >
                  {isCurrent ? "Plano Atual" : `Mudar para ${p.name}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-5 flex items-start gap-3">
          <Landmark className="w-5 h-5 text-blue-500 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">Pagamento e Ativação</p>
            <p className="text-muted-foreground mt-1">
              O pagamento é feito por transferência bancária. Envie o comprovativo para <strong>suporte@lumora.ao</strong> e
              receberá uma <strong>chave de ativação</strong> para inserir acima — a subscrição ativa de imediato pelo período pago.
              A mudança de plano é imediata e ajusta os módulos disponíveis no menu.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
